import { useMemo } from "react";

import { AmplitudeFlagKey, TicketingFormCategory } from "@simplyk/common";

import { FormType, LiveFormEditorFeatureFlags } from "./LiveFormEditorContext";

import { useCampaignLifeCycleStep } from "@/features/LiveFormEditor/Steps/StepCampaignLifeCycle/helpers/useCampaignLifeCycleStep";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { useAmplitudeFeatureFlag } from "@/common/hooks/amplitude/useAmplitudeFeatureFlag";
import { DonationFormWithUserOrOrgAccessOutput, TicketingOutput } from "@/common/types/trpc";
import { useCustomColorTileExperiment } from "@/components/LiveEditorElements/LiveEditorCustomColorCard/helpers/useCustomColorTileExperiment";

export const useInitLiveFormFeatureFlags = (
  formType: FormType,
  apiFormObject: TicketingOutput | DonationFormWithUserOrOrgAccessOutput
): LiveFormEditorFeatureFlags => {
  const { organization } = useCurrentUserContext();
  const isClosedWon = Boolean(organization?.fourthPaymentDate && organization?.stripeCustomAccountActiveAtUtc);
  const isTicketing = formType === "ticketing";
  const organizationId = apiFormObject.organizationId ?? undefined;

  const { isActive: isExperimentActive, loading: isDelightfulPagesLoading } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.DelightfulErLiveFormExperiment,
    organizationId,
    organizationProperties: { organization_zeffy_id: apiFormObject.organizationId || "" },
    skip: !apiFormObject.organizationId || isClosedWon,
  });
  const isDelightfulPagesActive = isClosedWon || isExperimentActive;

  const ticketing = apiFormObject as TicketingOutput;

  const isEventForm = isTicketing && ticketing.formCategory === TicketingFormCategory.Event;
  const { isActive: isAddOnsStepOn, loading: isAddOnsStepLoading } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.AddOns,
    organizationId,
    skip: !isEventForm,
  });

  const { isCampaignLifeCycleStepOn, isCampaignLifeCycleStepLoading } = useCampaignLifeCycleStep(
    isTicketing ? ticketing : null
  );
  const { isCustomColorTileActive, isCustomColorTileLoading } = useCustomColorTileExperiment(organizationId);

  return useMemo(
    () => ({
      isDelightfulPagesActive,
      isDelightfulPagesLoading,
      isAddOnsStepOn: isEventForm && isAddOnsStepOn,
      isAddOnsStepLoading: isEventForm && isAddOnsStepLoading,
      isCampaignLifeCycleStepOn,
      isCampaignLifeCycleStepLoading,
      isCustomColorTileActive,
      isCustomColorTileLoading,
    }),
    [
      isDelightfulPagesActive,
      isDelightfulPagesLoading,
      isEventForm,
      isAddOnsStepOn,
      isAddOnsStepLoading,
      isCampaignLifeCycleStepOn,
      isCampaignLifeCycleStepLoading,
      isCustomColorTileActive,
      isCustomColorTileLoading,
    ]
  );
};
